/**
 * 작성자 : 홍선영
 * 날짜 : 2024.08.12
 * 경로 : 설정관리 - CCTV관리 - 사용자 권한 정보
 */

import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { userState } from '../../../atoms';
import { IAuth, IModal } from 'customTypes';
import { InputTable } from '../../../assets/styles/InputTable';
import { BtnBlue } from '../../../components/Button';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import IssueGuide from '../../../components/IssueGuide';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import { ButtonsWrapper } from '../../sm_setting/setting7/cctvInfoStyle';
import { apiGet, apiPost } from '../../../services/_common';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import ClickableContainer from '../../s_cctv/S_cctv1/RealtimeCCTV/ClickableContainer';
import TableLamp from '../../../components/TableLamp';
import { trimArray } from '../../../utils/trimArray';
import { useSetAuth } from '../../../utils/useSetAuth';
import { scrollToNodeBottom } from '../../../utils/scrollToNodeBottom';
import { arraySortByAscdOrder } from '../../../utils/arraySortByAscdOrder';

const Root = styled.div`
  .jobtype-chip-container {
    height: 100%;
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
    .button {
      flex-shrink: 0;
      user-select: none;
      cursor: pointer;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.25rem;
      padding: 0 0.5rem;
      margin: auto 0;
      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 400;
        font-size: 1.5rem;
      }
    }
    .delete-button {
      background-color: var(--tonal-red);
      color: var(--filled-red);
      &:hover {
        outline: 0.25rem solid var(--filled-red);
        &:active {
          transform: scale(0.95);
        }
      }
    }
  }

  .fullwidth > div {
    width: 100%;
  }
`;

interface ICamGrant {
  hCd?: string;
  gCd: string;
  gName: string;
  ptzYn: string;
  capYn: string;
  recYn: string;
  recdownYn: string;
  useYn: string;
  eDate?: string;
  editor: string;
}

const UserCctvAuth = () => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const { hCd, sCd, userId } = userInfo;
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isTableInitialRender, setIsTableInitialRender] = useState(true); // 컴포넌트의 이니셜렌더 여부

  const {
    data: camGrantList,
    isLoading: isLoadingCamGrant,
    isFetching: isFetchingCamGrant,
    isError: isErrorCamGrant,
  } = useQuery(['getCamGrant', hCd], () => fetchCamGrantData(), {
    enabled: !!hCd && (sCd === '00000' || sCd === ''),
  });

  const [tableState, setTableState] = useState<ICamGrant[]>(camGrantList || []);
  const [initTableState, setInitTableState] = useState<ICamGrant[]>(camGrantList || []);

  useEffect(() => {
    //  로우가 1개 이상이고, 이니셜렌더가 아닌경우 스크롤을 하단으로 이동
    if (tableState.length > 0 && !isTableInitialRender) scrollToNodeBottom(scrollContainerRef);
  }, [tableState.length]);

  const fetchCamGrantData = async () => {
    try {
      const res = await apiGet({ path: '/cam/grant', req: { hCd } });
      const result = arraySortByAscdOrder(res.data.data, 'gCd');
      setTableState(result);
      setInitTableState(result);
      return result;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  const deleteAPI = async (obj: ICamGrant) => {
    const { gName, gCd, ptzYn, capYn, recYn, recdownYn, useYn } = obj;
    const result = { hCd, gCd, gName, ptzYn, capYn, recYn, recdownYn, useYn, delYn: 'Y', editor: userId };
    const path = '/cam/grant';
    const req = { camGrantlistReqDto: [result] };
    const res = await apiPost({ path, req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      toast.success(t('카메라 권한 리스트 삭제 성공'));
      setTableState(data);
    }
  };

  const onClickAdd = () => {
    const newRow: ICamGrant = {
      gCd: '',
      gName: '',
      ptzYn: 'N',
      capYn: 'N',
      recYn: 'N',
      recdownYn: 'N',
      useYn: 'Y',
      editor: '',
    };
    setIsTableInitialRender(false); // 스크롤 아래로 이동
    scrollToNodeBottom(scrollContainerRef);
    setTableState((prev: ICamGrant[]) => [...prev, newRow]);
  };

  // 원본데이터와 비교하여 수정사항이 있는지 판별하여 수정값이 있는 객체들을 배열로 리턴하는 함수
  const findDifferences = (original: any[], updated: any[], uniqueKey: string) => {
    const updates = updated.filter((upd) => {
      const orgEntry = original.find((org) => org[uniqueKey] === upd[uniqueKey]);
      return orgEntry ? Object.keys(upd).some((key) => upd[key] !== orgEntry[key]) : true;
    });

    return updates;
  };

  const onClickSave = async () => {
    try {
      setIsSaveClicked(true);
      // 수정사항이 있는 지 확인
      const updatedArray = findDifferences(initTableState, tableState, 'gCd');

      // 필수입력값 중 빈값이 있는 지 확인
      const emptyCheckKey: (keyof ICamGrant)[] = ['gName'];
      const emptyCheck: boolean[] = updatedArray.filter((el: ICamGrant) => {
        const check = emptyCheckKey.find((el2) => el[el2] === '');
        return check;
      });

      if (emptyCheck.length > 0) {
        toast.warning(t('필수입력값을 모두 입력하세요'));
        return;
      }

      if (updatedArray.length === 0) {
        toast.warning(t('신규/수정 사항이 없습니다'));
        return;
      }

      // 배열의 각 항목 선후행 공백제거
      const trimmed = trimArray(updatedArray);
      const result = trimmed.map(({ gName, gCd, ptzYn, capYn, recYn, recdownYn, useYn, writer }) => ({
        hCd,
        gCd,
        gName,
        ptzYn,
        capYn,
        recYn,
        recdownYn,
        useYn,
        delYn: 'N',
        ...(!gCd && { writer }),
        editor: userId,
      }));
      const path = '/cam/grant';
      const req = { camGrantlistReqDto: result };
      const res = await apiPost({ path, req });
      const { data, message, statusCode } = res.data;
      if (statusCode === 200) {
        toast.success(t(message));
        setTableState(data);
        setIsSaveClicked(false);
      }
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  const onClickDeleteRow = (index: number, obj: ICamGrant) => {
    if (obj.gCd) {
      // 삭제
      setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: () => deleteAPI(obj) }));
    } else {
      // 제거
      const updatedArray = [...tableState];
      updatedArray.splice(index, 1);
      setTableState(updatedArray);
    }
  };

  if (isErrorCamGrant) return <IssueGuide />;

  if (isLoadingCamGrant || isFetchingCamGrant)
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );

  return (
    <>
      <InputTable className='margin-left-05' style={{ flexShrink: '1' }} ref={scrollContainerRef}>
        <div className='thead'>
          <div className='tr'>
            <div className='trCol2p5 flex-center tableStickyNo'>No</div>
            <div className='trCol4 flex-center'>{t('코드')}</div>
            <div className='trCol12 flex-center tableStickyTitle content-overflow required'>{t('권한 명')}</div>
            <div className='trCol6 flex-center'>{t('PTZ 제어')}</div>
            <div className='trCol6 flex-center'>{t('캡쳐')}</div>
            <div className='trCol6 flex-center'>{t('녹화')}</div>
            <div className='trCol6 flex-center'>{t('녹화 다운로드')}</div>
            <div className='trCol6 flex-center'>{t('사용유무')}</div>
            <div className='trCol10 flex-center'>{t('수정일자')}</div>
            <div className='trCol6 flex-center'>{t('수정자')}</div>
            {auth.deleteAuth && <div className='trCol4 flex-center'> </div>}
          </div>
        </div>
        {tableState.length > 0 ? (
          <div className='table'>
            <div className='tbody'>
              {tableState?.map((el: ICamGrant, i: number) => (
                <InputTableRow
                  auth={auth}
                  key={`${i}_${el.gCd}`} //
                  rowData={el}
                  rowIndex={i}
                  tableState={tableState}
                  setTableState={setTableState}
                  onClickDeleteRow={onClickDeleteRow}
                  isSaveClicked={isSaveClicked}
                />
              ))}
            </div>
          </div>
        ) : (
          <IssueGuide />
        )}
      </InputTable>
      <ButtonsWrapper>
        {auth.createAuth && <BtnBlue onClick={onClickAdd}>{t('신규권한 추가')}</BtnBlue>}
        {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
      </ButtonsWrapper>
      <Portal openModal={openModal?.status}>{openModal && openModal.type === 'delete' ? <DeleteModal openModal={openModal} setOpenModal={setOpenModal} /> : undefined}</Portal>
    </>
  );
};

interface Props {
  auth: IAuth;
  rowData: ICamGrant;
  rowIndex: number;
  tableState: ICamGrant[];
  setTableState: Dispatch<SetStateAction<ICamGrant[]>>;
  onClickDeleteRow: any;
  isSaveClicked: boolean;
}

const InputTableRow = ({ auth, rowData, rowIndex, tableState, setTableState, onClickDeleteRow, isSaveClicked }: Props) => {
  const onClickCheckToggle = (key: keyof ICamGrant) => {
    const newArray = tableState.map((el: ICamGrant, i: number) => (i === rowIndex ? { ...el, [key]: el[key] === 'Y' ? 'N' : 'Y' } : el));
    setTableState(newArray);
  };

  return (
    <Root className='tr' role='button' tabIndex={0}>
      <div className='trCol2p5 flex-center tableStickyNo'>{rowIndex + 1}</div>
      <div className='trCol4 flex-center'>{rowData.gCd}</div>
      <div className='trCol12 flex-center tableStickyTitle content-overflow fullwidth'>
        <Input
          type='text'
          id='gName'
          name='gName'
          state={tableState}
          setState={setTableState}
          stateType={{ type: 'array', index: rowIndex }}
          getBorderStyle={isSaveClicked ? applyBorderStyle(rowData.gName, 'red', 'gName') : undefined}
          useFlag
          maxLength={50}
        />
      </div>
      <div className='trCol6 flex-center'>
        <ClickableContainer
          onClick={() => onClickCheckToggle('ptzYn')} //
          button={<TableLamp use={rowData.ptzYn === 'Y'} color='blue' />}
        />
      </div>
      <div className='trCol6 flex-center'>
        <ClickableContainer
          onClick={() => onClickCheckToggle('capYn')} //
          button={<TableLamp use={rowData.capYn === 'Y'} color='blue' />}
        />
      </div>
      <div className='trCol6 flex-center'>
        <ClickableContainer
          onClick={() => onClickCheckToggle('recYn')} //
          button={<TableLamp use={rowData.recYn === 'Y'} color='blue' />}
        />
      </div>
      <div className='trCol6 flex-center'>
        <ClickableContainer
          onClick={() => onClickCheckToggle('recdownYn')} //
          button={<TableLamp use={rowData.recdownYn === 'Y'} color='blue' />}
        />
      </div>
      <div className='trCol6 flex-center'>
        <ClickableContainer
          onClick={() => onClickCheckToggle('useYn')} //
          button={<TableLamp use={rowData.useYn === 'Y'} />}
        />
      </div>
      <div className='trCol10 flex-center'>{rowData.eDate}</div>
      <div className='trCol6 flex-center'>{rowData.editor}</div>
      <div className='trCol4 flex-start scroll jobtype-chip-container'>
        {auth.deleteAuth && (
          <div
            className='button delete-button' //
            onClick={() => onClickDeleteRow(rowIndex, rowData)}
            role='button'
            tabIndex={0}
          >
            <span className='material-symbols-rounded'>delete</span>
          </div>
        )}
      </div>
    </Root>
  );
};

export default UserCctvAuth;
